import { App, Empty, Flex, Skeleton } from "antd"
import React, { useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import {
  DocumentReviewRejectModal,
  type DocumentReviewRejectModalMode
} from "../../../components/Documents/DocumentReviewRejectModal.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import {
  approveProposal,
  approveProposal as approveProposalThunk,
  cancelProposal,
  cancelProposal as cancelProposalThunk,
  duplicateProposal,
  fetchProposal,
  fetchProposals,
  rejectProposal,
  rejectProposal as rejectProposalThunk
} from "../redux/proposals.thunks.ts"
import { ProposalViewBody } from "./components/ProposalViewBody.tsx"
import { ProposalViewHeader } from "./components/ProposalViewHeader.tsx"

function ProposalViewPage({
  proposal,
  fetching,
  saving,
  viewLeadAllowed,
  getProposals,
  getProposal,
  rejectProposal,
  cancelProposal,
  approveProposal,
  duplicateProposal
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const { modal } = App.useApp()
  const id = useParams()?.id as string
  const [rejectModalMode, setRejectModalMode] =
    useState<DocumentReviewRejectModalMode | null>(null)

  useEffect(() => {
    if (!id) return
    getProposal(id)
  }, [id])

  useEffect(() => {
    if (!proposal) return
    generateDocumentTitle(proposal?.proposal_id)
  }, [proposal])

  if (fetching) {
    return <Skeleton.Node className={"size-full"} />
  }

  if (!proposal) {
    return (
      <Empty
        className={"mx-auto"}
        description={"The proposal was not found."}
      />
    )
  }

  const handleRejectSubmit = async (reason: string) => {
    const action =
      rejectModalMode === "cancel"
        ? await cancelProposal({
            id: proposal.id,
            reason: reason.trim(),
            notes: ""
          })
        : await rejectProposal({
            id: proposal.id,
            reason: reason.trim()
          })

    const isSuccess =
      rejectModalMode === "cancel"
        ? cancelProposalThunk.fulfilled.match(action)
        : rejectProposalThunk.fulfilled.match(action)

    if (isSuccess) {
      setRejectModalMode(null)
      navigate(RoutesPaths.proposal.list)
      getProposals()
    }
  }

  const handleApproveProposal = async () => {
    const action = await approveProposal(proposal.id)
    if (approveProposalThunk.fulfilled.match(action)) {
      navigate(RoutesPaths.proposal.list)
      getProposals()
    }
  }

  const handleDuplicateProposal = () => {
    modal.confirm({
      centered: true,
      title: "Confirm duplication",
      content: "Are you sure you want to duplicate this proposal?",
      cancelText: "Close",
      okText: "Duplicate",
      onOk: async () => {
        const newProposal = await duplicateProposal(proposal?.id)?.unwrap()

        if (!newProposal) return

        const href = proposal?.is_version_2
          ? RoutesPaths.proposalDocument.edit(newProposal?.id)
          : RoutesPaths.proposal.edit(newProposal?.id)

        navigate(href)
      }
    })
  }

  return (
    <Flex vertical gap={"small"} flex={1}>
      {rejectModalMode && (
        <DocumentReviewRejectModal
          mode={rejectModalMode}
          saving={saving}
          onSubmit={handleRejectSubmit}
          onClose={() => setRejectModalMode(null)}
        />
      )}

      <ProposalViewHeader
        proposal={proposal}
        saving={saving}
        viewLeadAllowed={viewLeadAllowed}
        onApproveProposal={handleApproveProposal}
        onUnderRevision={() => setRejectModalMode("revision")}
        onCancelReview={() => setRejectModalMode("cancel")}
        onDuplicateProposal={handleDuplicateProposal}
      />
      <ProposalViewBody proposal={proposal} />
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  proposal: state.proposals.proposal,
  fetching: state.proposals.fetching,
  saving: state.proposals.saveFetching,
  viewLeadAllowed:
    state?.auth?.userInfo?.permissions?.codes?.leads?.leads?.lead_view
})

const mapDispatchToProps = {
  getProposals: fetchProposals,
  getProposal: fetchProposal,
  rejectProposal,
  cancelProposal,
  approveProposal,
  duplicateProposal
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ProposalViewPage)
